/**
 * Pure rules for playing two films in lockstep (before/after compare).
 *
 * The audible film leads; the muted one follows and is snapped back when it drifts more than
 * RESYNC_TOLERANCE_SECONDS (a 2-frame budget at 24 fps — below what an A/B listener notices,
 * above the jitter browsers show between two independently decoding elements).
 */

export type AudibleSide = "left" | "right";

export const RESYNC_TOLERANCE_SECONDS = 0.08;

export function needsResync(
  leaderTime: number,
  followerTime: number,
  tolerance = RESYNC_TOLERANCE_SECONDS,
): boolean {
  if (!Number.isFinite(leaderTime) || !Number.isFinite(followerTime))
    return false;
  return Math.abs(leaderTime - followerTime) > tolerance;
}

export function clampSeek(time: number, duration: number): number {
  if (!Number.isFinite(time) || time < 0) return 0;
  if (!Number.isFinite(duration) || duration <= 0) return time;
  return Math.min(time, duration);
}

/** The longer of the two films (they are the same cut, but containers can differ by a frame). */
export function commonDuration(...durations: (number | undefined)[]): number {
  const finite = durations.filter(
    (d): d is number => typeof d === "number" && Number.isFinite(d) && d > 0,
  );
  return finite.length ? Math.max(...finite) : 0;
}

export function formatClock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const whole = Math.floor(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

export const otherSide = (side: AudibleSide): AudibleSide =>
  side === "left" ? "right" : "left";
